import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import {Point, isPointTuple} from '@gl/lit/lib/point.js';
import {change} from '@gl/lit/utils/commands/index.js';
import type CanvasElement from './index.js';

/** Key to match on when listening to delete elements. */
export const DELETE_TRIGGER = 'Backspace';

export type Model = {
  key: string;
  focused: boolean;
  attached: boolean;
  selected: boolean;
  position: Point;
};

export const Msg = makeVariants({
  select: Variant<void>()('select'),
  deselect: Variant<void>()('deselect'),
  delete: Variant<void>()('delete'),
  focus: Variant<void>()('focus'),
  unfocus: Variant<void>()('unfocus'),
  setAttached: Variant<boolean>()('set-attached'),
  changePosition: Variant<Point>()('change-position'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function onInit(host: CanvasElement) {
  addEventListener('keyup', (event: KeyboardEvent) => {
    if (!host.model.selected) return;
    if (event.key === DELETE_TRIGGER) host.delete();
  });
}

function init(
  props: InitialProperties,
  cmd: Cmd,
  model?: Model
): ImmutableResult<Model> {
  const initialPosition = props.get('initialPosition');
  const key = <string>props.get('key');

  return [
    {
      ...model,
      key,
      attached: false,
      focused: false,
      selected: <boolean>props.get('selected') ?? false,
      position: isPointTuple(initialPosition)
        ? Point.fromTuple(initialPosition)
        : Point.from(0, 0),
    },
    cmd((host: CanvasElement) => onInit(host)),
  ];
}

// -- Update

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-attached':
      return {...model, attached: msg.value} as const;
    case 'change-position':
      return [
        {...model, position: model.position.add(msg.value)},
        cmd((host: CanvasElement) => {
          host.dispatchEvent(host.createEvent('moved'));
        }),
      ] as const;
    case 'select':
      return [{...model, selected: true}, cmd(change)] as const;
    case 'deselect':
      return [{...model, selected: false}, cmd(change)] as const;
    case 'focus':
      return {...model, focused: true} as const;
    case 'unfocus':
      return {...model, focused: false} as const;
    case 'delete':
      return [model, cmd((h: CanvasElement) => h.delete())];
  }
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
};
